import React, { useState, useEffect } from "react";
import { message, Button, Tooltip, Tabs } from "antd";
import {
  BellOutlined,
  SendOutlined,
  TagOutlined,
  GiftOutlined,
  NotificationOutlined,
  HeartOutlined,
  StarOutlined,
  ThunderboltOutlined,
  RedoOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { broadcastNotificationApi, getNotificationHistoryApi, markActivityReadApi } from "../../services/api";
import PromoEmailComposer from "./PromoEmailComposer";
import TransactionalEmailLog from "./TransactionalEmailLog";

// ─── Quick-fill templates ─────────────────────────────────────────────────────
const TEMPLATES = [
  {
    icon: <GiftOutlined />,
    label: "Flash Sale",
    color: "#f04d4d",
    title: "🔥 Flash Sale — Up to 50% Off!",
    body: "Limited time offer on baby clothes, toys & more. Shop now before it's gone!",
    type: "OFFER",
  },
  {
    icon: <TagOutlined />,
    label: "New Arrivals",
    color: "#4a90d9",
    title: "✨ New Arrivals Just Dropped!",
    body: "Fresh styles for your little one are here. Explore the latest collection today.",
    type: "NEW_ARRIVAL",
  },
  {
    icon: <StarOutlined />,
    label: "Weekend Deal",
    color: "#f5a623",
    title: "🛍️ Weekend Special Offer",
    body: "Enjoy exclusive weekend discounts on selected baby products. Grab yours now!",
    type: "OFFER",
  },
  {
    icon: <HeartOutlined />,
    label: "Health Tip",
    color: "#52c41a",
    title: "💚 Baby Health Tip",
    body: "Did you know? Regular tummy time helps strengthen your baby's neck and shoulder muscles.",
    type: "HEALTH_TIP",
  },
  {
    icon: <ThunderboltOutlined />,
    label: "App Update",
    color: "#722ed1",
    title: "🚀 New Feature Available",
    body: "We've added new features to make your parenting journey easier. Update the app now!",
    type: "ANNOUNCEMENT",
  },
  {
    icon: <NotificationOutlined />,
    label: "Reminder",
    color: "#13c2c2",
    title: "📋 Vaccination Reminder",
    body: "Don't forget to check your child's upcoming vaccination schedule in the app.",
    type: "REMINDER",
  },
];

// Campaign types the compose form can send — do not add order-lifecycle
// types here, Object.keys(TYPE_COLORS) below drives the TYPE button list.
const TYPE_COLORS = {
  OFFER:        "#f04d4d",
  NEW_ARRIVAL:  "#4a90d9",
  HEALTH_TIP:   "#52c41a",
  ANNOUNCEMENT: "#722ed1",
  REMINDER:     "#13c2c2",
  GENERAL:      "#6b7280",
};

// Send-history badges additionally cover order-lifecycle pushes (OrdersService),
// which are never composed here but do show up in the log with a real orderId —
// kept separate from TYPE_COLORS so they can't leak into the compose buttons.
const HISTORY_TYPE_COLORS = {
  ...TYPE_COLORS,
  ORDER_PLACED:           "#534ab7",
  ORDER_NEEDS_ACCEPTANCE: "#f5a623",
  ORDER_VENDOR_REJECTED:  "#f04d4d",
  ORDER_STATUS_UPDATED:   "#1565C0",
  ORDER_CANCELLED:        "#dc2626",
};

const formatType = (type) => (type || "").replace(/_/g, " ");

// The original single-purpose "Push Notifications" composer — now one tab of
// the Notifications page rather than the whole page. Behavior unchanged.
function PushNotificationsPanel() {
  const navigate = useNavigate();
  const [title,   setTitle]   = useState("");
  const [body,    setBody]    = useState("");
  const [type,    setType]    = useState("GENERAL");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);   // persisted broadcast history from backend
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await getNotificationHistoryApi();
      setHistory(res.data?.data || []);
    } catch {
      message.error("Failed to load notification history");
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => { fetchHistory(); }, []);

  const applyTemplate = (tpl) => {
    setTitle(tpl.title);
    setBody(tpl.body);
    setType(tpl.type);
  };

  const handleResend = (item) => {
    setTitle(item.title);
    setBody(item.body);
    setType(item.type || "GENERAL");
    message.info("Loaded into composer — edit if needed, then send.");
  };

  // Same deep-link as the header bell and Dashboard's Recent Activity — only
  // order-lifecycle pushes carry an orderId (OFFER/ANNOUNCEMENT/... broadcasts
  // don't), so plain campaign rows just aren't clickable.
  const handleRowClick = (item) => {
    if (!item.orderId) return;
    markActivityReadApi(item.orderId).catch(() => {});
    navigate("/orders", { state: { openOrderId: item.orderId } });
  };

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      message.warning("Please fill in both title and message.");
      return;
    }
    setLoading(true);
    try {
      const res = await broadcastNotificationApi({ title: title.trim(), body: body.trim(), type });
      const info = res.data?.data || "Notification sent";
      message.success(info);
      fetchHistory();
      // Delivery happens asynchronously (queued → Firebase), so the row just
      // fetched above still shows 0/0 — refetch once more after it's had time
      // to actually go out instead of leaving a permanently stale snapshot.
      setTimeout(fetchHistory, 4000);
      setTitle("");
      setBody("");
      setType("GENERAL");
    } catch (err) {
      message.error(err?.response?.data?.data || "Failed to send notification");
    } finally {
      setLoading(false);
    }
  };

  // ─── Preview card ────────────────────────────────────────────────────────────
  const PreviewCard = () => (
    <div style={{
      background: "#1c1c1e",
      borderRadius: 16,
      padding: "14px 16px",
      color: "#fff",
      display: "flex",
      gap: 12,
      alignItems: "flex-start",
      boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
      maxWidth: 340,
    }}>
      <div style={{
        width: 44, height: 44, borderRadius: 10,
        background: "linear-gradient(135deg,#4a90d9,#2d5aa8)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 22, flexShrink: 0,
      }}><img src="/icon.png" alt="Kidodom" style={{ width: 28, height: 28, borderRadius: 6, objectFit: "contain" }} /></div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>
          {title || "Notification title"}
        </div>
        <div style={{ fontSize: 12, opacity: 0.75, lineHeight: 1.4, wordBreak: "break-word" }}>
          {body || "Your message preview will appear here..."}
        </div>
        <div style={{ fontSize: 10, opacity: 0.45, marginTop: 4 }}>Kidodom · now</div>
      </div>
    </div>
  );

  return (
    <div style={{ padding: "24px 0" }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, display: "flex", alignItems: "center", gap: 10 }}>
          <BellOutlined style={{ color: "#4a90d9" }} />
          Push Notifications
        </h2>
        <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: 13 }}>
          Compose and broadcast a push notification to all users with the app installed.
        </p>
      </div>

      <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>

        {/* ── Left: Compose ──────────────────────────────────────────────────── */}
        <div style={{ flex: "1 1 480px", minWidth: 320 }}>

          {/* Quick templates */}
          <div style={{
            background: "#fff", borderRadius: 12,
            boxShadow: "0 1px 6px rgba(0,0,0,0.08)", padding: 20, marginBottom: 20,
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 12 }}>
              Quick Templates
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {TEMPLATES.map((tpl) => (
                <button
                  key={tpl.label}
                  onClick={() => applyTemplate(tpl)}
                  style={{
                    display: "flex", alignItems: "center", gap: 6,
                    padding: "6px 12px", borderRadius: 20,
                    border: `1.5px solid ${tpl.color}20`,
                    background: `${tpl.color}10`,
                    color: tpl.color,
                    fontSize: 12, fontWeight: 600, cursor: "pointer",
                    transition: "0.15s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = `${tpl.color}20`)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = `${tpl.color}10`)}
                >
                  {tpl.icon} {tpl.label}
                </button>
              ))}
            </div>
          </div>

          {/* Compose form */}
          <div style={{
            background: "#fff", borderRadius: 12,
            boxShadow: "0 1px 6px rgba(0,0,0,0.08)", padding: 20,
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 16 }}>
              Compose Notification
            </div>

            {/* Type */}
            <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 6 }}>
              TYPE
            </label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
              {Object.keys(TYPE_COLORS).map((t) => (
                <button
                  key={t}
                  onClick={() => setType(t)}
                  style={{
                    padding: "4px 12px", borderRadius: 12, fontSize: 11, fontWeight: 700,
                    cursor: "pointer", transition: "0.15s",
                    border: type === t ? `2px solid ${TYPE_COLORS[t]}` : "2px solid transparent",
                    background: type === t ? `${TYPE_COLORS[t]}18` : "#f3f4f6",
                    color: type === t ? TYPE_COLORS[t] : "#6b7280",
                  }}
                >
                  {t.replace(/_/g, " ")}
                </button>
              ))}
            </div>

            {/* Title */}
            <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 6 }}>
              TITLE
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. 🔥 Flash Sale — 50% Off!"
              maxLength={60}
              style={{
                width: "100%", padding: "10px 12px", borderRadius: 8,
                border: "1.5px solid #e5e7eb", fontSize: 14,
                outline: "none", boxSizing: "border-box", marginBottom: 4,
                transition: "border-color 0.2s",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#4a90d9")}
              onBlur={(e)  => (e.target.style.borderColor = "#e5e7eb")}
            />
            <div style={{ fontSize: 11, color: "#9ca3af", textAlign: "right", marginBottom: 14 }}>
              {title.length}/60
            </div>

            {/* Body */}
            <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 6 }}>
              MESSAGE
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your notification message here..."
              maxLength={200}
              rows={4}
              style={{
                width: "100%", padding: "10px 12px", borderRadius: 8,
                border: "1.5px solid #e5e7eb", fontSize: 14, resize: "vertical",
                outline: "none", boxSizing: "border-box", marginBottom: 4,
                fontFamily: "inherit", transition: "border-color 0.2s",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#4a90d9")}
              onBlur={(e)  => (e.target.style.borderColor = "#e5e7eb")}
            />
            <div style={{ fontSize: 11, color: "#9ca3af", textAlign: "right", marginBottom: 20 }}>
              {body.length}/200
            </div>

            {/* Send button */}
            <button
              onClick={handleSend}
              disabled={loading || !title.trim() || !body.trim()}
              style={{
                width: "100%", padding: "12px 0",
                background: loading || !title.trim() || !body.trim()
                  ? "#e5e7eb"
                  : "linear-gradient(135deg,#4a90d9,#2d5aa8)",
                color: loading || !title.trim() || !body.trim() ? "#9ca3af" : "#fff",
                border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700,
                cursor: loading || !title.trim() || !body.trim() ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                transition: "0.2s",
              }}
            >
              {loading ? (
                <>
                  <span style={{
                    width: 16, height: 16, border: "2px solid #fff",
                    borderTopColor: "transparent", borderRadius: "50%",
                    display: "inline-block", animation: "spin 0.6s linear infinite",
                  }} />
                  Sending...
                </>
              ) : (
                <><SendOutlined /> Send to All Users</>
              )}
            </button>
          </div>
        </div>

        {/* ── Right: Preview + History ────────────────────────────────────────── */}
        <div style={{ flex: "0 0 320px", minWidth: 280 }}>

          {/* Phone preview */}
          <div style={{
            background: "#fff", borderRadius: 12,
            boxShadow: "0 1px 6px rgba(0,0,0,0.08)", padding: 20, marginBottom: 20,
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 14 }}>
              Preview
            </div>
            <PreviewCard />
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 10 }}>
              This is how the notification will appear on the user's device.
            </div>
          </div>

          {/* Send history (persisted) */}
          <div style={{
            background: "#fff", borderRadius: 12,
            boxShadow: "0 1px 6px rgba(0,0,0,0.08)", padding: 20,
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "flex", alignItems: "center", gap: 6 }}>
                <HistoryOutlined /> Send History
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {historyLoading && <span style={{ fontSize: 11, color: "#9ca3af" }}>Loading…</span>}
                <Button size="small" onClick={fetchHistory} disabled={historyLoading}>
                  Refresh
                </Button>
              </div>
            </div>

            {history.length === 0 ? (
              <div style={{ fontSize: 12, color: "#9ca3af" }}>No notifications sent yet.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: 420, overflowY: "auto" }}>
                {history.map((s) => (
                  <div
                    key={s.id}
                    onClick={() => handleRowClick(s)}
                    style={{
                      padding: "10px 12px", borderRadius: 8, background: "#f9fafb",
                      borderLeft: `3px solid ${HISTORY_TYPE_COLORS[s.type] || "#6b7280"}`,
                      display: "flex", justifyContent: "space-between", gap: 8,
                      cursor: s.orderId ? "pointer" : "default",
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{s.title}</div>
                      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, wordBreak: "break-word" }}>{s.body}</div>
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>
                        {formatType(s.type)} · {s.deviceCount ?? 0} queued · {s.sentAt ? new Date(s.sentAt).toLocaleString() : ""}
                      </div>
                      <div style={{ fontSize: 11, marginTop: 4, display: "flex", gap: 10 }}>
                        <span style={{ color: "#16a34a", fontWeight: 600 }}>✓ {s.successCount ?? 0} delivered</span>
                        {(s.failureCount ?? 0) > 0 && (
                          <span style={{ color: "#dc2626", fontWeight: 600 }}>✗ {s.failureCount} failed</span>
                        )}
                        {(s.deviceCount ?? 0) > (s.successCount ?? 0) + (s.failureCount ?? 0) && (
                          <span style={{ color: "#9ca3af" }}>
                            ⏳ {s.deviceCount - (s.successCount ?? 0) - (s.failureCount ?? 0)} pending
                          </span>
                        )}
                      </div>
                      {s.lastError && (
                        <div style={{ fontSize: 10, color: "#dc2626", marginTop: 3, wordBreak: "break-word" }}>
                          Last error: {s.lastError}
                        </div>
                      )}
                    </div>
                    <Tooltip title="Load into composer to resend">
                      <Button
                        size="small"
                        icon={<RedoOutlined />}
                        onClick={() => handleResend(s)}
                        style={{ flexShrink: 0 }}
                      />
                    </Tooltip>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Spinner keyframe */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default function Notifications() {
  return (
    <div style={{ padding: "0 24px", maxWidth: 1100, margin: "0 auto" }}>
      <Tabs
        items={[
          { key: "push", label: "Push Notifications", children: <PushNotificationsPanel /> },
          { key: "email", label: "Promotional Email", children: <PromoEmailComposer /> },
          { key: "log", label: "Transactional Email Log", children: <TransactionalEmailLog /> },
        ]}
      />
    </div>
  );
}
